import { GridTable } from "@dashboard/components/GridTable";
import Link from "@dashboard/components/Link";
import { dealerLocatorUrl, googleAppsUrl, tieredPricingUrl } from "@dashboard/envSettings/urls";
import { ExtensionAvatar } from "@dashboard/extensions/components/ExtensionAvatar";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import { Box, sprinkles, Text } from "@saleor/macaw-ui-next";
import React from "react";

import useCheckoutTermsPage from "../hooks/useCheckoutTermsPage";
import { ThirdPartyIntegrations } from "../hooks/useThirdPartyIntegrations";

const GoogleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const GenericIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#9AA0A6" />
    <path d="M12 8v8M8 12h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

interface ThirdPartyAppRowProps {
  name: string;
  href?: string;
  logo: React.ReactNode;
}

const ThirdPartyAppRow: React.FC<ThirdPartyAppRowProps> = ({ name, href, logo }) => {
  const ExtensionLink = ({ children }: { children: React.ReactNode }) => {
    if (!href) {
      return (
        <Box display="flex" alignItems="center" __padding="5px 20px">
          {children}
        </Box>
      );
    }

    return (
      <Link
        href={href}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 20px",
          textDecoration: "none",
          color: "inherit",
        }}
        className={sprinkles({
          backgroundColor: {
            default: "default1",
            hover: "default2",
          },
        })}
      >
        {children}
      </Link>
    );
  };

  return (
    <GridTable.Row data-test-id="third-party-app-row">
      <GridTable.Cell padding={0}>
        <ExtensionLink>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <ExtensionAvatar>{logo}</ExtensionAvatar>
            <Text
              size={4}
              fontWeight="bold"
              __maxWidth="200px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {name}
            </Text>
          </Box>
        </ExtensionLink>
      </GridTable.Cell>
    </GridTable.Row>
  );
};

interface ThirdPartyIntegrationsSectionProps {
  data: ThirdPartyIntegrations;
  loading: boolean;
  error?: string | null;
}

export const ThirdPartyIntegrationsSection: React.FC<ThirdPartyIntegrationsSectionProps> = ({
  data,
  loading,
  error,
}) => {
  const { pageId: checkoutTermsPageId } = useCheckoutTermsPage();

  if (loading) {
    return (
      <Box marginTop={6}>
        <Text>Loading third-party integrations...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box marginTop={6}>
        <Text color="critical1">Error loading third-party integrations: {error}</Text>
      </Box>
    );
  }

  const formatAppName = (appName: string) => {
    return appName
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Box __marginLeft="-24px" __marginRight="-24px" paddingBottom={6}>
      <GridTable>
        <GridTable.Colgroup>
          <GridTable.Col />
        </GridTable.Colgroup>
        <GridTable.Body>
          {/* Google group */}
          {data.external.length > 0 && (
            <ThirdPartyAppRow name="Google" href={googleAppsUrl()} logo={<GoogleIcon />} />
          )}

          {/* SMTP - Always visible */}
          <ThirdPartyAppRow name="SMTP" href={ExtensionsPaths.smtp} logo={<GenericIcon />} />

          {/* Currency Settings - Always visible */}
          <ThirdPartyAppRow
            name="Currency Settings"
            href={ExtensionsPaths.currencySettings}
            logo={<GenericIcon />}
          />

          {/* CAPTCHA Settings - Always visible */}
          <ThirdPartyAppRow
            name="CAPTCHA Settings"
            href={ExtensionsPaths.captchaSettings}
            logo={<GenericIcon />}
          />

          {/* Tiered Pricing - Always visible */}
          <ThirdPartyAppRow
            name="Tiered Pricing"
            href={tieredPricingUrl()}
            logo={<GenericIcon />}
          />

          {/* Individual internal apps */}
          {data.internal
            .filter(app => app.app_name !== "smtp")
            .map(app => (
              <ThirdPartyAppRow
                key={app.app_name}
                name={formatAppName(app.app_name)}
                href={
                  app.app_name === "dealer_locator"
                    ? dealerLocatorUrl()
                    : app.app_name === "tiered_pricing"
                      ? tieredPricingUrl()
                      : app.app_name === "checkout_questions"
                        ? `/third-party-integrations/checkout-question`
                        : app.app_name === "checkout_terms_conditions" && checkoutTermsPageId
                          ? `/models/${checkoutTermsPageId}`
                          : app.app_name === "shipping_restrictions"
                            ? `/third-party-integrations/shipping-restrictions`
                            : app.app_name === "will_call"
                              ? ExtensionsPaths.willCallShipping
                              : `/third-party-integrations/${app.app_name}`
                }
                logo={<GenericIcon />}
              />
            ))}
        </GridTable.Body>
      </GridTable>
    </Box>
  );
};
